/*
 * Client-side diagnostic breadcrumb buffer.
 *
 * Captures nav + api + state + exception events in a rolling FIFO buffer,
 * then ships them to the API when a notable event fires (forced logout,
 * error boundary, 5xx, network error, pagination failure, manual report).
 *
 * Gated by a per-investigation flag fetched from the API. Zero-deps, no side
 * effects at import time — `initDiagnostics()` must be called before any
 * `addBreadcrumb()` takes effect.
 *
 * Production-safe: redacts payloads against a strict allowlist; never logs
 * patient names/DOB/personalData; bounded in size (events + bytes); uses
 * fetch keepalive so a forced-logout flush still lands during navigation.
 */

import {
    apiBaseUrl as inferApiBaseUrl,
    jwt as safeGetAuthToken,
    decodeJwtClaims,
    safeRandomUUID,
} from './identity';

const DEFAULT_CONFIG = {
    maxBufferSize: 100,
    maxBufferBytes: 100 * 1000,
    flushRateLimitMs: 10 * 1000,
};

// SHER-499: diagnostics breadcrumbs are ALWAYS-ON. There is no per-investigation opt-in; every
// session buffers and ships its breadcrumbs. Volume is bounded by the buffer size/byte caps, the
// flush rate-limit, and host-side log retention (7-day window) — not by gating who may report.

const ALLOWED_DATA_KEYS = new Set([
    'url',
    'method',
    'status',
    'durationMs',
    'correlationId',
    'requestId',
    'code',
    'errorName',
    'errorMessage',
    'route',
    'uuid',
    'permissionKey',
    'jwtExp',
    'jwtIat',
    'jwtIatMs',
    'researcherUuid',
    'jwtLen',
    'jwtSegments',
    'jwtShape',
    'crossTab',
    'pageOffset',
    'pageLimit',
    'pageNumber',
    'pageSize',
    'offset',
    'limit',
    'returnedCount',
    'total',
    'hasMore',
    'atOffset',
    'pagesCompleted',
    'memoryUsedMB',
    'memoryLimitMB',
    'componentStackSummary',
    'previousUserExists',
    'ok',
    'reason',
    'origin',
    // SHER-499 pre-crash app-state snapshot keys (getAppStateSnapshot, flat scalars). Keep in sync
    // with the server ALLOWED_DATA_KEYS (sherwood-api diagnostics.controller.ts).
    'investigationUuid',
    'investigationName',
    'patientsLoaded',
    'pendingSyncActions',
    'patientsDownloading',
    'patientsLoading',
    'online',
    'activeForms',
    'activeFormFields',
    'hasResearcherKey',
    'hasInvestigationKey',
    'appVersion',
    'appGitSha',
]);

const state = {
    enabled: false,
    investigationUuid: null,
    apiBaseUrl: '',
    apiEndpoint: '/diagnostics/breadcrumbs',
    sessionId: null,
    buffer: [],
    bufferBytes: 0,
    config: { ...DEFAULT_CONFIG },
    lastFlushMs: 0,
    lastFlushFingerprint: null,
    correlationCounter: 0,
    clientBuildCommit: null,
};

function getOrCreateSessionId() {
    try {
        const existing = window.sessionStorage?.getItem('sherwoodDiagSessionId');
        if (existing) return existing;
        const fresh = safeRandomUUID();
        window.sessionStorage?.setItem('sherwoodDiagSessionId', fresh);
        return fresh;
    } catch (_) {
        return safeRandomUUID();
    }
}

function redactData(data) {
    if (!data || typeof data !== 'object') return undefined;
    const out = {};
    for (const key of Object.keys(data)) {
        if (!ALLOWED_DATA_KEYS.has(key)) continue;
        const value = data[key];
        if (value === undefined || value === null) continue;
        if (typeof value === 'object') continue; // allowlist only takes scalars
        if (key === 'url' && typeof value === 'string') {
            out[key] = redactUrl(value);
        } else if (typeof value === 'string' && value.length > 400) {
            out[key] = value.slice(0, 400);
        } else {
            out[key] = value;
        }
    }
    return out;
}

function redactUrl(url) {
    try {
        // Preserve path; drop query except limit/offset.
        const questionIdx = url.indexOf('?');
        const path = questionIdx >= 0 ? url.slice(0, questionIdx) : url;
        const qs = questionIdx >= 0 ? url.slice(questionIdx + 1) : '';
        const keep = [];
        if (qs) {
            for (const pair of qs.split('&')) {
                const eq = pair.indexOf('=');
                const key = eq >= 0 ? pair.slice(0, eq) : pair;
                if (key === 'limit' || key === 'offset') keep.push(pair);
            }
        }
        return keep.length ? `${path}?${keep.join('&')}` : path;
    } catch (_) {
        return '[redacted]';
    }
}

function measureSize(entry) {
    try {
        return JSON.stringify(entry).length;
    } catch (_) {
        return 0;
    }
}

function trimBuffer() {
    while (
        state.buffer.length > state.config.maxBufferSize ||
        state.bufferBytes > state.config.maxBufferBytes
    ) {
        const dropped = state.buffer.shift();
        if (!dropped) break;
        state.bufferBytes -= dropped._bytes || 0;
    }
}

function nextCorrelationId() {
    state.correlationCounter = (state.correlationCounter + 1) & 0xffff;
    return `${state.sessionId || 'nosess'}-${Date.now().toString(36)}-${state.correlationCounter.toString(36)}`;
}

function addBreadcrumb(type, msg, data) {
    // SHER-499: always fill the bounded ring buffer, even when the investigation isn't opted in,
    // so a crash flush always has a trail to send. The buffer is size/byte-capped (trimBuffer) and
    // redacted, and it is only ever SHIPPED on a crash reason (or for opted-in investigations), so
    // recording it for everyone costs only bounded memory, never an unwanted network send.
    const entry = {
        ts: new Date().toISOString(),
        type: typeof type === 'string' ? type : 'unknown',
        msg: typeof msg === 'string' ? msg.slice(0, 200) : '',
        data: redactData(data),
    };
    entry._bytes = measureSize(entry);
    state.buffer.push(entry);
    state.bufferBytes += entry._bytes;
    trimBuffer();
}

function buildPayload(reason) {
    return {
        investigationUuid: state.investigationUuid,
        sessionId: state.sessionId,
        reason: typeof reason === 'string' ? reason : 'unknown',
        clientBuildCommit: state.clientBuildCommit,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
        breadcrumbs: state.buffer.map(({ _bytes, ...rest }) => rest),
    };
}

function fingerprint(payload) {
    const first = payload.breadcrumbs[0];
    const firstTs = first ? first.ts : '-';
    return `${payload.reason}|${firstTs}|${payload.breadcrumbs.length}`;
}

function flush(reason) {
    // SHER-499: always-on — no opt-in gate. The rate-limit + fingerprint guards below still apply.
    if (!state.buffer.length) return;
    const now = Date.now();
    if (state.lastFlushMs && now - state.lastFlushMs < state.config.flushRateLimitMs) {
        // Rate-limited — same window. Drop silently.
        return;
    }
    const payload = buildPayload(reason);
    const fp = fingerprint(payload);
    if (fp === state.lastFlushFingerprint && now - state.lastFlushMs < state.config.flushRateLimitMs * 2) {
        return;
    }
    state.lastFlushMs = now;
    state.lastFlushFingerprint = fp;

    // SHER-499: now that flushing is always-on, a crash can fire before any investigation was
    // selected (initDiagnostics not yet run → apiBaseUrl empty). Fall back to the inferred API base
    // so the POST hits the API origin, not the client's, instead of being lost.
    const url = `${state.apiBaseUrl || inferApiBaseUrl()}${state.apiEndpoint}`;
    const body = JSON.stringify(payload);
    const authToken = safeGetAuthToken();

    try {
        // Raw fetch (not axios) so we can't recursively breadcrumb ourselves,
        // and keepalive so the request survives page unload / logout redirects.
        fetch(url, {
            method: 'POST',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                ...(authToken ? { Authorization: authToken } : {}),
            },
            body,
        }).catch(() => {
            // Losing a breadcrumb flush is acceptable; do NOT retry.
        });
    } catch (_) { /* ignore */ }
}

async function initDiagnostics({ investigationUuid, apiBaseUrl, clientBuildCommit } = {}) {
    if (!investigationUuid) return;
    // Re-init is a no-op unless the investigation changed.
    if (state.enabled && state.investigationUuid === investigationUuid) return;

    state.investigationUuid = investigationUuid;
    state.apiBaseUrl = apiBaseUrl || state.apiBaseUrl || inferApiBaseUrl();
    state.sessionId = getOrCreateSessionId();
    state.clientBuildCommit = clientBuildCommit || state.clientBuildCommit;

    try {
        const token = safeGetAuthToken();
        const resp = await fetch(
            `${state.apiBaseUrl}/diagnostics/investigation/${encodeURIComponent(investigationUuid)}/config`,
            {
                method: 'GET',
                headers: token ? { Authorization: token } : {},
            }
        );
        if (!resp.ok) {
            state.enabled = false;
            return;
        }
        const cfg = await resp.json();
        if (!cfg || cfg.breadcrumbsEnabled !== true) {
            state.enabled = false;
            return;
        }
        state.config = {
            maxBufferSize: Number(cfg.maxBufferSize) || DEFAULT_CONFIG.maxBufferSize,
            maxBufferBytes: Number(cfg.maxBufferBytes) || DEFAULT_CONFIG.maxBufferBytes,
            flushRateLimitMs: Number(cfg.flushRateLimitMs) || DEFAULT_CONFIG.flushRateLimitMs,
        };
        state.enabled = true;
    } catch (_) {
        state.enabled = false;
    }
}

function parseJwtSafe(token) {
    try {
        const claims = decodeJwtClaims(token);
        return {
            jwtExp: typeof claims.exp === 'number' ? claims.exp : undefined,
            jwtIat: typeof claims.iat === 'number' ? claims.iat : undefined,
            researcherUuid: typeof claims.uuid === 'string' ? claims.uuid : undefined,
        };
    } catch (_) {
        return {};
    }
}

// Classify the *shape* of a JWT-in-storage WITHOUT decoding or revealing it.
// `parseJwtSafe` returns {} for a malformed token, so a corrupted/absent JWT is
// invisible in breadcrumbs — exactly the CHOM case where /researcher/validate is
// rejected with "jwt malformed" / "jwt must be provided". This distinguishes the
// failure modes (absent vs the literal string "undefined"/"null" written by a bad
// writer vs a truncated/garbage value vs a structurally-valid token) using only
// the length and segment count — no token contents ever leave the device.
function jwtShape(token) {
    if (token == null) return { jwtShape: 'absent' };
    if (typeof token !== 'string') return { jwtShape: 'nonstring' };
    if (token === '') return { jwtShape: 'empty', jwtLen: 0 };
    if (token === 'undefined' || token === 'null') {
        return { jwtShape: `literal-${token}`, jwtLen: token.length };
    }
    const segments = token.split('.').length;
    return {
        jwtLen: token.length,
        jwtSegments: segments,
        // A well-formed JWT is exactly 3 dot-separated segments. Anything else is
        // structurally malformed (the server will reject it as "jwt malformed").
        jwtShape: segments === 3 ? 'jwt-3seg' : 'malformed-segments',
    };
}

function isEnabled() {
    return state.enabled;
}

// Compact, synchronous snapshot of the current session id + the tail of the
// breadcrumb buffer, formatted for INLINE inclusion in the Slack crash report.
// Why: on a white-screen crash the async breadcrumb flush to /diagnostics/breadcrumbs
// can be dropped (401 on the JWT-gated endpoint, keepalive failure, rate-limit),
// so the nav trail that shows HOW the user reached the crash never lands server-side.
// The Slack message, by contrast, always arrives. Carrying the trail inline there
// makes the reliable channel self-sufficient. Returns only the same non-personal,
// already-redacted fields the breadcrumbs themselves hold (routes/types/msgs).
function getCrashContext(limit = 15) {
    try {
        const tail = state.buffer.slice(-limit).map((b) => {
            const d = b.data || {};
            let line;
            if (b.type === 'nav') line = `nav:${d.route || b.msg || '?'}`;
            else if (b.type === 'api') line = `api:${d.method || ''}${d.route || ''}${d.status ? `(${d.status})` : ''}`;
            else if (b.type === 'exception') line = `exc:${(b.msg || d.errorName || '?')}`;
            else line = `${b.type}:${b.msg || ''}`;
            return line.slice(0, 80);
        });
        return { sessionId: state.sessionId, enabled: state.enabled, trail: tail };
    } catch (_) {
        return { sessionId: null, enabled: false, trail: [] };
    }
}

export {
    initDiagnostics,
    addBreadcrumb,
    flush,
    nextCorrelationId,
    parseJwtSafe,
    jwtShape,
    isEnabled,
    getCrashContext,
};
